import {
  AmbientLight,
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  Group,
  Line,
  LineBasicMaterial,
  PerspectiveCamera,
  Points,
  Scene,
  Vector3,
} from 'three';
import type { BodyDescriptor } from '../physics/types';
import { eclipticToScene } from '../physics/frame';
import { BodyMesh } from './BodyMesh';
import { OrbitLine } from './OrbitLine';
import { Belt } from './Belt';
import { createSunGroup } from './Sun';
import { createStarfield } from './Skybox';
import { StarMap } from './StarMap';
import { RealStarfield } from './RealStarfield';
import { AtmosphereSky } from './AtmosphereSky';
import { HosekWilkieSky } from './HosekWilkieSky';
import { LocationPin } from './LocationPin';
import { MessierLayer } from './MessierLayer';
import { IAUBoundaries } from './IAUBoundaries';
import { LocalTerrain } from './LocalTerrain';
import { SpacecraftLayer } from './Spacecraft';
import { LagrangePointsLayer } from './LagrangePoints';
import type { ScaleController } from '../controls/ScaleController';
import type { SimulationClock } from '../time/SimulationClock';
import { gmstRad } from '../physics/topocentric';
import { moonLibrationDeg } from '../physics/lunarPosition';
import { buildNBodyFromDescriptors, NBodyAdapter, type NBodySimulation } from '../physics/nbody';
import { STARS_AND_PLANETS, SUN } from '../data/bodies';
import { MOONS } from '../data/moons';
import { DWARFS } from '../data/dwarfs';
import { BELTS } from '../data/belts';
import { COMETS } from '../data/comets';
import { ASTEROIDS } from '../data/asteroids';
// SatelliteLayer is lazy-loaded — see ensureSatellites() below. Importing
// only the type keeps satellite.js (~21 KB gz) out of the initial bundle.
// The chunk is fetched on the first observer-mode entry, where Earth-orbiting
// satellites are actually visible. Heliocentric users never download it.
import type { SatelliteLayer } from './SatelliteLayer';
import { RealismState } from './RealismSettings';
import { AtmosphereOverlay } from './AtmosphereOverlay';
import { MilkyWay } from './MilkyWay';
import { ZodiacalLight } from './ZodiacalLight';
import { MeteorShowers } from './MeteorShowers';
import { CometTails } from './CometTails';
import { CelestialGrids } from './CelestialGrids';
import { LunarMansionsLayer } from './LunarMansionsLayer';
import { GalacticDisk } from './GalacticDisk';
import { LocalGroupGalaxies } from './LocalGroupGalaxies';
import { SharplessLayer } from './SharplessLayer';
import { NGCFullLayer } from './NGCFullLayer';
import { AbellLayer } from './AbellLayer';
import { ScopeReticle } from './ScopeReticle';
import { HygCloud } from './HygCloud';
import { ExoplanetHosts } from './ExoplanetHosts';
import { ExoplanetSystemView } from './ExoplanetSystemView';

interface BodyEntry {
  descriptor: BodyDescriptor;
  mesh: BodyMesh;
  orbit: OrbitLine | null;
  /** Where this body's group lives in the scene graph (sun root or parent body's group). */
  parentGroup: Group;
}

/**
 * Top-level orchestrator: builds the scene from data, updates positions each
 * frame from JD, and reacts to scale changes by rebuilding visuals.
 */
export class SolarSystem {
  readonly scene = new Scene();
  /** Heliocentric root: sun is at origin; planets are children. */
  private readonly heliocentric = new Group();

  private readonly bodies = new Map<string, BodyEntry>();
  private readonly belts: Belt[] = [];
  private readonly orbitsRoot = new Group();        // planet / dwarf orbits
  private readonly cometOrbitsRoot = new Group();   // comet + asteroid orbits (more chaotic, opt-in)
  private starfield: Points | null = null;
  private starMap: StarMap | null = null;
  private realStarfield: RealStarfield | null = null;
  private atmosphereSky: AtmosphereSky | null = null;
  private hosekWilkieSky: HosekWilkieSky | null = null;
  private skyModel: 'preetham' | 'hosek-wilkie' = 'preetham';
  private locationPin: LocationPin | null = null;
  private messierLayer: MessierLayer | null = null;
  private iauBoundaries: IAUBoundaries | null = null;
  private localTerrain: LocalTerrain | null = null;
  private spacecraft: SpacecraftLayer | null = null;
  private lagrangePoints: LagrangePointsLayer | null = null;
  private satellites: SatelliteLayer | null = null;
  private nbodySim: NBodySimulation | null = null;
  private atmosphereOverlay: AtmosphereOverlay | null = null;
  private milkyWay: MilkyWay | null = null;
  private zodiacalLight: ZodiacalLight | null = null;
  private meteorShowers: MeteorShowers | null = null;
  private cometTails: CometTails | null = null;
  private celestialGrids: CelestialGrids | null = null;
  private lunarMansions: LunarMansionsLayer | null = null;
  private galacticDisk: GalacticDisk | null = null;
  private localGroup: LocalGroupGalaxies | null = null;
  private sharpless: SharplessLayer | null = null;
  private ngcFull: NGCFullLayer | null = null;
  private abell: AbellLayer | null = null;
  private scopeReticle: ScopeReticle | null = null;
  private hygCloud: HygCloud | null = null;
  private exoplanetHosts: ExoplanetHosts | null = null;
  private exoplanetView: ExoplanetSystemView | null = null;
  readonly realism = new RealismState();
  private originalPropagators = new Map<string, import('../physics/types').OrbitPropagator | null>();

  /**
   * Sub-groups parented to a planet's group for hierarchical bodies (moons).
   * Moon distances use a different (much larger) scale than solar distances,
   * because at solar scale the Moon would be inside Earth's mesh.
   */
  private readonly moonOrbitsRoot = new Group();

  private lastJd: number;
  private prevScaleMode: string;
  private prevFrame: string;
  private orbitsVisible: boolean = true;
  private geocentricOrbits: Map<string, Line> = new Map();

  constructor(
    private scaler: ScaleController,
    clock: SimulationClock,
  ) {
    this.lastJd = clock.getJd();
    this.prevScaleMode = scaler.getMode();
    this.prevFrame = scaler.getFrame();

    // Lightweight random starfield as immediate fallback while the real
    // catalog loads; we hide it once the catalog is ready.
    this.starfield = createStarfield(2000);
    this.scene.add(this.starfield);

    this.realStarfield = new RealStarfield();
    this.scene.add(this.realStarfield.object);
    // Lazy-load: defer the 600 KB BSC catalogue fetch until the browser is
    // idle (or after 1.5 s, whichever comes first). On first paint this
    // shaves perceived load time noticeably; users in heliocentric view
    // never need the full catalogue anyway.
    const triggerLoad = () => {
      this.realStarfield!.load('/stars-bsc.json').then(() => {
        if (this.starfield) {
          const mat = this.starfield.material as { opacity: number };
          mat.opacity = 0;
          this.starfield.visible = false;
        }
      });
    };
    if ('requestIdleCallback' in window) {
      (window as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void })
        .requestIdleCallback?.(triggerLoad, { timeout: 1500 });
    } else {
      setTimeout(triggerLoad, 1500);
    }

    this.starMap = new StarMap();
    this.scene.add(this.starMap.points);
    this.scene.add(this.starMap.lines);
    this.scene.add(this.starMap.labels);

    this.messierLayer = new MessierLayer();
    this.scene.add(this.messierLayer.object);

    // Sharpless 2 emission-nebula layer — opt-in via Realism toggle.
    // Layer mounts immediately but loads data lazily on first toggle-on
    // to avoid the ~9 KB fetch for users who never enable it.
    this.sharpless = new SharplessLayer();
    this.scene.add(this.sharpless.object);

    // Full NGC + IC catalogue layer — also opt-in (445 KB on first enable).
    this.ngcFull = new NGCFullLayer();
    this.scene.add(this.ngcFull.object);

    // Abell galaxy-cluster catalogue (v0.6) — 2712 entries, ~80 KB.
    this.abell = new AbellLayer();
    this.scene.add(this.abell.object);

    // INDI/ASCOM telescope-bridge reticle (v0.7 Tier 1). The reticle
    // mounts always but stays hidden until the bridge starts pushing
    // pointing fixes — so unused it costs only a Mesh + a LineSegments
    // in the scene graph.
    this.scopeReticle = new ScopeReticle();
    this.scene.add(this.scopeReticle.object);

    this.iauBoundaries = new IAUBoundaries();
    this.scene.add(this.iauBoundaries.object);
    this.iauBoundaries.load('/iau-boundaries.json');

    this.localTerrain = new LocalTerrain();
    this.scene.add(this.localTerrain.object);

    this.atmosphereSky = new AtmosphereSky();
    this.atmosphereSky.attach(this.scene);
    // Hosek-Wilkie sky lives alongside the Preetham sky — only one is
    // visible at a time. Lazy-attached but not lazy-constructed (the
    // shader compile is fast enough we don't gain from deferring it).
    this.hosekWilkieSky = new HosekWilkieSky();
    this.hosekWilkieSky.attach(this.scene);
    this.atmosphereOverlay = new AtmosphereOverlay();
    this.scene.add(this.atmosphereOverlay.mesh);
    this.milkyWay = new MilkyWay();
    this.scene.add(this.milkyWay.mesh);
    this.zodiacalLight = new ZodiacalLight();
    this.zodiacalLight.attach(this.scene);
    this.meteorShowers = new MeteorShowers();
    this.scene.add(this.meteorShowers.object);
    this.cometTails = new CometTails();
    this.scene.add(this.cometTails.object);
    this.celestialGrids = new CelestialGrids();
    this.scene.add(this.celestialGrids.object);
    this.lunarMansions = new LunarMansionsLayer();
    this.scene.add(this.lunarMansions.object);
    // 3D Milky Way disk — only visible at the galactic tier of the
    // scale-tier zoom-out. Lazy-built once the layer is requested so it
    // doesn't burn ~30k vertex-buffer entries on first paint.
    this.galacticDisk = new GalacticDisk();
    this.scene.add(this.galacticDisk.group);
    // Local Group galaxy billboards — visible at the same galactic tier
    // as the disk. M31 / M33 / LMC / SMC + a handful of dwarfs.
    this.localGroup = new LocalGroupGalaxies();
    this.scene.add(this.localGroup.group);
    // HYG 3D point cloud — created here but data is lazy-loaded the
    // first time the neighbourhood tier is requested (~600 KB JSON).
    this.hygCloud = new HygCloud();
    this.scene.add(this.hygCloud.group);
    // Halos for the 12 curated exoplanet systems, drawn in the same
    // HYG-cloud frame so they overlay the actual host star positions.
    this.exoplanetHosts = new ExoplanetHosts();
    this.scene.add(this.exoplanetHosts.group);
    // Scene-swap manager for "land on Trappist-1 / Proxima / etc."
    // Lazy-builds each system's host + planet meshes the first time
    // it's activated. Hidden by default; activated via the InfoPanel
    // "Visit system" button.
    this.exoplanetView = new ExoplanetSystemView(this.scaler);
    this.scene.add(this.exoplanetView.group);
    // Ambient kept low so the day/night terminator on textured planets is
    // visible, but high enough to make the night side faintly readable.
    this.scene.add(new AmbientLight(0x6878a0, 0.55));

    this.scene.add(this.heliocentric);
    this.heliocentric.add(this.orbitsRoot);
    this.heliocentric.add(this.cometOrbitsRoot);
    this.heliocentric.add(this.moonOrbitsRoot);
    this.cometOrbitsRoot.visible = false; // off by default — opt-in to declutter

    this.buildSun();
    this.buildPlanets();
    this.buildDwarfs();
    this.buildComets();
    this.buildMoons();
    this.buildBelts();

    this.spacecraft = new SpacecraftLayer(this.scaler);
    this.heliocentric.add(this.spacecraft.group);

    // Lagrange points (L1–L5) for Sun–Earth, Sun–Jupiter, Earth–Moon.
    // Built from the live body registry so positions track each frame.
    const descriptorMap = new Map<string, BodyDescriptor>();
    for (const [id, entry] of this.bodies) descriptorMap.set(id, entry.descriptor);
    this.lagrangePoints = new LagrangePointsLayer(this.scaler, descriptorMap);
    this.lagrangePoints.setVisible(false); // off by default — opt-in
    this.heliocentric.add(this.lagrangePoints.group);

    // Earth-orbiting satellites are NOT constructed here — see
    // ensureSatellites(). At heliocentric scale the LEO constellation is
    // sub-pixel anyway; only observer-mode users need it.

    this.update(clock.getJd());
  }

  /**
   * Lazy-load and construct the SatelliteLayer. Called on first observer-mode
   * entry. Subsequent calls are no-ops (memoized). The async chunk fetch
   * (satellite.js ~21 KB gz) typically completes in 100–300 ms; until then
   * `this.satellites` stays null and `update()` skips the satellite step.
   */
  private satellitesPromise: Promise<void> | null = null;
  ensureSatellites(): Promise<void> {
    if (this.satellitesPromise) return this.satellitesPromise;
    this.satellitesPromise = (async () => {
      const { SatelliteLayer } = await import('./SatelliteLayer');
      this.satellites = new SatelliteLayer();
      const earthBody = this.bodies.get('earth');
      if (earthBody) earthBody.mesh.group.add(this.satellites.object);
      // Apply current visibility state from the toggle, since the user may
      // have flipped it during the load.
      const toggle = document.getElementById('toggle-satellites') as HTMLInputElement | null;
      if (toggle) this.satellites.setVisible(toggle.checked);
      // Upgrade the bundled snapshot TLEs to live CelesTrak elements in the
      // background. The layer already renders from snapshots, so this is
      // fire-and-forget — never block the load on it.
      void this.satellites.refreshFromCelesTrak();
    })();
    return this.satellitesPromise;
  }

  private buildSun(): void {
    const r = this.scaler.radiusKm(SUN.physical.radiusKm) * this.scaler.sunMultiplier();

    // BodyMesh creates an inner sphere (we hide it) + label + pickable.
    // We then add the actual emissive sphere + glow + PointLight into the same group,
    // so the whole thing moves/positions/picks as one unit at the origin.
    const mesh = new BodyMesh(SUN, r);
    mesh.mesh.visible = false;
    const { core, glow, light } = createSunGroup(r);
    mesh.group.add(core);
    mesh.group.add(glow);
    mesh.group.add(light);

    this.heliocentric.add(mesh.group);

    this.bodies.set(SUN.id, {
      descriptor: SUN,
      mesh,
      orbit: null,
      parentGroup: this.heliocentric,
    });
  }

  private buildPlanets(): void {
    for (const body of STARS_AND_PLANETS) {
      if (body.id === 'sun') continue;
      this.addSolarBody(body);
    }
  }

  private buildDwarfs(): void {
    for (const body of DWARFS) {
      this.addSolarBody(body);
    }
  }

  private buildComets(): void {
    for (const body of COMETS) {
      this.addSolarBody(body);
    }
    for (const body of ASTEROIDS) {
      this.addSolarBody(body);
    }
  }

  /**
   * Add a body at runtime (e.g. from a Horizons fetch). Same code path as
   * the constructor's body builders. Returns true if added; false if a body
   * with the same id already exists.
   */
  addRuntimeBody(body: BodyDescriptor): boolean {
    if (this.bodies.has(body.id)) return false;
    this.addSolarBody(body);
    return true;
  }

  /** Remove a previously-added runtime body by id. */
  removeRuntimeBody(id: string): boolean {
    const entry = this.bodies.get(id);
    if (!entry) return false;
    this.heliocentric.remove(entry.mesh.group);
    if (entry.orbit) {
      this.orbitsRoot.remove(entry.orbit.line);
      this.cometOrbitsRoot.remove(entry.orbit.line);
    }
    this.bodies.delete(id);
    return true;
  }

  private addSolarBody(body: BodyDescriptor): void {
    const r = this.scaler.radiusKm(body.physical.radiusKm);
    const mesh = new BodyMesh(body, r);
    this.heliocentric.add(mesh.group);

    let orbit: OrbitLine | null = null;
    if (body.propagator?.elements) {
      orbit = new OrbitLine(body.propagator.elements, { color: orbitColor(body), segments: 256 });
      orbit.rebuild(this.scaler);
      // Comets + asteroids land in their own group so users can toggle them
      // independently of the (much tidier) planet/dwarf orbit set.
      const target = body.category === 'comet' ? this.cometOrbitsRoot : this.orbitsRoot;
      target.add(orbit.line);
    }

    this.bodies.set(body.id, {
      descriptor: body,
      mesh,
      orbit,
      parentGroup: this.heliocentric,
    });
  }

  private buildMoons(): void {
    for (const moon of MOONS) {
      const parent = this.bodies.get(moon.parentId!);
      if (!parent) continue;

      const r = this.scaler.radiusKm(moon.physical.radiusKm);
      const mesh = new BodyMesh(moon, r);
      parent.mesh.group.add(mesh.group);

      let orbit: OrbitLine | null = null;
      if (moon.propagator?.elements) {
        orbit = new OrbitLine(moon.propagator.elements, {
          color: 0x6a7a8a,
          segments: 96,
          isMoon: true,
        });
        orbit.rebuild(this.scaler);
        parent.mesh.group.add(orbit.line);
      }

      this.bodies.set(moon.id, {
        descriptor: moon,
        mesh,
        orbit,
        parentGroup: parent.mesh.group,
      });
    }
  }

  private buildBelts(): void {
    for (const def of BELTS) {
      const belt = new Belt(def);
      this.belts.push(belt);
      this.heliocentric.add(belt.mesh);
    }
  }

  // ------------------------------------------------------------------------
  // Update loop
  // ------------------------------------------------------------------------

  update(jd: number): void {
    const dDays = jd - this.lastJd;
    this.lastJd = jd;

    // In geocentric mode, all heliocentric bodies have Earth's position
    // subtracted before scaling — Earth ends up at origin, Sun ~1 AU on the
    // opposite side, and outer planets trace epicycle loops over time.
    const isGeocentric = this.scaler.getFrame() === 'geocentric';
    let earthHelioPos: Vector3 | null = null;
    if (isGeocentric) {
      const earth = this.bodies.get('earth');
      if (earth?.descriptor.propagator) {
        earthHelioPos = earth.descriptor.propagator.stateAt(jd).position.clone();
      }
    }

    for (const entry of this.bodies.values()) {
      const desc = entry.descriptor;

      // Sun has no propagator — in geocentric mode we still need to position
      // it at -earthHelioPos (scaled). Heliocentric mode leaves it at origin.
      if (desc.id === 'sun') {
        if (isGeocentric && earthHelioPos) {
          const sunGeoPos = earthHelioPos.clone().multiplyScalar(-1);
          const len = sunGeoPos.length();
          const scaledLen = this.scaler.distanceAU(len);
          if (len > 0) sunGeoPos.multiplyScalar(scaledLen / len);
          entry.mesh.setPosition(eclipticToScene(sunGeoPos));
        } else {
          entry.mesh.setPosition(new Vector3(0, 0, 0));
        }
        entry.mesh.advanceRotation(dDays);
        continue;
      }

      if (!desc.propagator) continue;

      const sv = desc.propagator.stateAt(jd);
      const isMoon = desc.category === 'moon';

      // Geocentric transformation: subtract Earth's heliocentric position from
      // any non-moon body. (Moons stay relative to their parent — handled by
      // the scene-graph parent transform.)
      let pos = sv.position;
      if (isGeocentric && earthHelioPos && !isMoon) {
        pos = pos.clone().sub(earthHelioPos);
      }

      const len = pos.length();
      const scaledLen = isMoon
        ? this.scaler.moonDistanceAU(len)
        : this.scaler.distanceAU(len);

      const scenePos = pos.clone();
      if (len > 0) scenePos.multiplyScalar(scaledLen / len);
      const out = eclipticToScene(scenePos);
      entry.mesh.setPosition(out);

      // Earth: lock rotation to GMST so the texture aligns with topocentric
      // observer math. Other bodies just spin at their natural rate.
      if (desc.id === 'earth') {
        entry.mesh.setRotationAngle(gmstRad(jd));
      } else {
        entry.mesh.advanceRotation(dDays);
      }

      // Moon: apply optical libration so we see slightly more than 50 % of the
      // surface over the synodic month. Uses Meeus ch. 53 (computed from the
      // Moon's actual ecliptic position vs the orientation of its mean equator)
      // rather than the sin-wave proxy this used to be — so the wobble lines up
      // correctly with eclipses and the real lunar-node cycle.
      if (desc.id === 'moon') {
        const lib = moonLibrationDeg(jd);
        entry.mesh.setLibration(
          lib.latitudeDeg * Math.PI / 180,
          lib.longitudeDeg * Math.PI / 180,
        );
      }
    }

    for (const belt of this.belts) {
      belt.update(jd, this.scaler);
    }

    if (this.spacecraft) this.spacecraft.update(jd);
    if (this.lagrangePoints) this.lagrangePoints.update(jd);
    if (this.cometTails) this.cometTails.update(jd, this.scaler);
    if (this.satellites) {
      // Sun direction in ecliptic frame, as seen from Earth (for shadow test).
      // Heliocentric: sunHelio = origin → sun is at (0,0,0); from earth, the
      // direction *to* the sun is (sun - earth) → in ecliptic that's just
      // -earthHelio. We pass a unit vector.
      const earthEntry = this.bodies.get('earth');
      const earthPropagator = earthEntry?.descriptor.propagator;
      let sunDirEcl: Vector3 | undefined;
      if (earthPropagator) {
        const sv = earthPropagator.stateAt(jd);
        sunDirEcl = sv.position.clone().multiplyScalar(-1).normalize();
      }
      this.satellites.update(jd, this.scaler, sunDirEcl);
    }

    if (this.scaler.getMode() !== this.prevScaleMode) {
      this.handleScaleChange();
      this.prevScaleMode = this.scaler.getMode();
    }
    if (this.scaler.getFrame() !== this.prevFrame) {
      this.handleFrameChange();
      this.prevFrame = this.scaler.getFrame();
    }
  }

  private handleScaleChange(): void {
    for (const entry of this.bodies.values()) {
      const r = this.scaler.radiusKm(entry.descriptor.physical.radiusKm);
      const finalR = entry.descriptor.id === 'sun' ? r * this.scaler.sunMultiplier() : r;
      entry.mesh.rebuildForRadius(finalR);
      if (entry.orbit) entry.orbit.rebuild(this.scaler);
    }
    // Geocentric trajectory lines depend on scale → rebuild them too.
    if (this.scaler.getFrame() === 'geocentric') this.buildGeocentricOrbitLines();
    if (this.spacecraft) this.spacecraft.rebuild();
    if (this.lagrangePoints) this.lagrangePoints.rebuild(this.lastJd);
    this.exoplanetView?.rebuild();
    this.refreshLocationPin();
  }

  private handleFrameChange(): void {
    const isGeo = this.scaler.getFrame() === 'geocentric';
    // Toggle the analytic Kepler ellipses (only valid heliocentrically).
    for (const entry of this.bodies.values()) {
      if (entry.orbit) entry.orbit.line.visible = !isGeo && this.orbitsVisible;
    }
    // Show/hide / rebuild geocentric trajectory lines.
    if (isGeo) {
      this.buildGeocentricOrbitLines();
    } else {
      this.disposeGeocentricOrbitLines();
    }
  }

  private buildGeocentricOrbitLines(): void {
    this.disposeGeocentricOrbitLines();
    const earth = this.bodies.get('earth');
    if (!earth?.descriptor.propagator) return;
    const earthProp = earth.descriptor.propagator;
    const earthPeriod = 365.25;
    const jdNow = this.lastJd || 2451545.0;

    for (const [id, entry] of this.bodies) {
      if (id === 'earth') continue;
      if (entry.descriptor.category === 'moon') continue;
      const prop = entry.descriptor.propagator;
      if (!prop) {
        // Sun gets a special straight-back orbit: 1-year ellipse around Earth
        // (mirror image of Earth's heliocentric orbit). Build it like other bodies
        // so the user sees the apparent solar path.
        if (id === 'sun') {
          const verts: number[] = [];
          const sampleDays = earthPeriod;
          const steps = 200;
          for (let i = 0; i <= steps; i++) {
            const jd = jdNow - sampleDays / 2 + (i / steps) * sampleDays;
            const ep = earthProp.stateAt(jd).position;
            const sunGeo = ep.clone().multiplyScalar(-1);
            const len = sunGeo.length();
            const scaledLen = this.scaler.distanceAU(len);
            if (len > 0) sunGeo.multiplyScalar(scaledLen / len);
            const p = eclipticToScene(sunGeo);
            verts.push(p.x, p.y, p.z);
          }
          this.addGeocentricLine(id, verts, orbitColor(entry.descriptor), entry.descriptor.category);
        }
        continue;
      }
      const period = prop.elements?.periodDays ?? 365;
      // Synodic period = how often the body returns to same phase relative
      // to Earth = how often the apparent loop pattern repeats.
      const synodic = Math.abs(1 / Math.abs(1 / period - 1 / earthPeriod));
      const sampleDays = Math.max(120, Math.min(synodic, 1200));
      const steps = 240;
      const verts: number[] = [];
      for (let i = 0; i <= steps; i++) {
        const jd = jdNow - sampleDays / 2 + (i / steps) * sampleDays;
        const bp = prop.stateAt(jd).position;
        const ep = earthProp.stateAt(jd).position;
        const rel = bp.clone().sub(ep);
        const len = rel.length();
        const scaledLen = this.scaler.distanceAU(len);
        if (len > 0) rel.multiplyScalar(scaledLen / len);
        const p = eclipticToScene(rel);
        verts.push(p.x, p.y, p.z);
      }
      this.addGeocentricLine(id, verts, orbitColor(entry.descriptor), entry.descriptor.category);
    }
  }

  private addGeocentricLine(id: string, verts: number[], color: number, category: BodyDescriptor['category']): void {
    const geom = new BufferGeometry();
    geom.setAttribute('position', new Float32BufferAttribute(verts, 3));
    const mat = new LineBasicMaterial({
      color, transparent: true, opacity: 0.55, depthWrite: false,
    });
    const line = new Line(geom, mat);
    line.frustumCulled = false;
    line.visible = this.orbitsVisible;
    this.geocentricOrbits.set(id, line);
    const target = category === 'comet' ? this.cometOrbitsRoot : this.orbitsRoot;
    target.add(line);
  }

  private disposeGeocentricOrbitLines(): void {
    for (const line of this.geocentricOrbits.values()) {
      // Lines may have been added to either parent — let parent removal happen
      // automatically via Object3D's parent tracking.
      line.parent?.remove(line);
      line.geometry.dispose();
      (line.material as LineBasicMaterial).dispose();
    }
    this.geocentricOrbits.clear();
  }

  // ------------------------------------------------------------------------
  // Public accessors
  // ------------------------------------------------------------------------

  getBody(id: string): BodyEntry | undefined {
    return this.bodies.get(id);
  }

  getAllBodies(): BodyEntry[] {
    return Array.from(this.bodies.values());
  }

  getPickableMeshes(): { object: any; bodyId: string }[] {
    const list: { object: any; bodyId: string }[] = [];
    for (const entry of this.bodies.values()) {
      list.push({ object: entry.mesh.pickable, bodyId: entry.descriptor.id });
    }
    return list;
  }

  setOrbitsVisible(visible: boolean): void {
    this.orbitsVisible = visible;
    this.orbitsRoot.visible = visible;
    for (const entry of this.bodies.values()) {
      if (entry.descriptor.category === 'moon' && entry.orbit) {
        entry.orbit.setVisible(visible);
      }
    }
  }

  /**
   * Comet + asteroid orbits live in their own group so the user can keep
   * them off by default — their high eccentricity / inclination makes the
   * planet view look like a tangle when shown together.
   */
  setCometOrbitsVisible(visible: boolean): void {
    this.cometOrbitsRoot.visible = visible;
  }

  setLabelsVisible(visible: boolean): void {
    for (const entry of this.bodies.values()) {
      entry.mesh.setLabelVisible(visible);
    }
  }

  /**
   * Keep all label sprites at constant pixel size by reverse-projecting
   * camera distance into world units each frame.
   */
  private readonly _tmpLabelPos = new Vector3();
  updateLabelSizes(camera: PerspectiveCamera, canvasHeightPx: number): void {
    const fovRad = (camera.fov * Math.PI) / 180;
    for (const entry of this.bodies.values()) {
      entry.mesh.group.getWorldPosition(this._tmpLabelPos);
      const dist = this._tmpLabelPos.distanceTo(camera.position);
      entry.mesh.updateLabelScreenSize(dist, fovRad, canvasHeightPx);
    }
  }

  setBeltsVisible(visible: boolean): void {
    for (const belt of this.belts) belt.setVisible(visible);
  }

  /** Set the starfield's overall opacity (1 = fully visible, 0 = invisible). */
  setStarfieldOpacity(opacity: number): void {
    if (this.realStarfield) this.realStarfield.setOpacity(opacity);
    if (this.starMap) this.starMap.setOpacity(opacity);
    if (this.messierLayer) this.messierLayer.setOpacity(opacity);
  }

  setMessierVisible(visible: boolean): void {
    if (this.messierLayer) this.messierLayer.setVisible(visible);
  }

  setMessierStylized(stylized: boolean): void {
    if (this.messierLayer) this.messierLayer.setStylized(stylized);
  }

  setMessierRealAngularSize(enabled: boolean): void {
    if (this.messierLayer) this.messierLayer.setRealAngularSize(enabled);
  }

  updateMessierAngularScale(fovDeg: number, canvasHeight: number): void {
    if (this.messierLayer) this.messierLayer.updateAngularScale(fovDeg, canvasHeight);
  }

  getMessierLayer(): MessierLayer | null { return this.messierLayer; }

  setIAUBoundariesVisible(visible: boolean): void {
    if (this.iauBoundaries) this.iauBoundaries.setVisible(visible);
  }

  getLocalTerrain(): LocalTerrain | null { return this.localTerrain; }

  setConstellationsVisible(visible: boolean): void {
    if (this.starMap) this.starMap.setLinesVisible(visible);
  }

  setStarLabelsVisible(visible: boolean): void {
    if (this.starMap) this.starMap.setLabelsVisible(visible);
  }

  /** Access the named-star map (for the per-frame epoch update that drives
   *  proper motion + annual aberration on star + constellation positions). */
  getStarMap(): StarMap | null { return this.starMap; }

  setStarfieldMagnitudeLimit(magLimit: number): void {
    if (this.realStarfield) this.realStarfield.setMagnitudeLimit(magLimit);
  }

  setBortleScale(bortle: number): void {
    if (this.realStarfield) this.realStarfield.setBortleScale(bortle);
    if (this.milkyWay) this.milkyWay.setBortle(bortle);
    if (this.atmosphereOverlay) this.atmosphereOverlay.setBortle(bortle);
  }

  getRealStarfield(): RealStarfield | null { return this.realStarfield; }
  getSatelliteLayer(): SatelliteLayer | null { return this.satellites; }
  getAtmosphereOverlay(): AtmosphereOverlay | null { return this.atmosphereOverlay; }

  setMilkyWayVisible(visible: boolean): void {
    if (this.milkyWay) this.milkyWay.setVisible(visible);
  }
  setMilkyWayBortle(bortle: number): void {
    if (this.milkyWay) this.milkyWay.setBortle(bortle);
  }
  setGridVisible(kind: 'equatorial' | 'ecliptic' | 'galactic' | 'horizontal', v: boolean): void {
    this.celestialGrids?.setVisible(kind, v);
  }
  getCelestialGrids(): CelestialGrids | null { return this.celestialGrids; }
  setLunarMansionsVisible(v: boolean): void {
    this.lunarMansions?.setVisible(v);
  }
  setZodiacalLightVisible(v: boolean): void { this.zodiacalLight?.setVisible(v); }
  setMeteorShowersVisible(v: boolean): void { this.meteorShowers?.setVisible(v); }
  getZodiacalLight(): ZodiacalLight | null { return this.zodiacalLight; }
  getMeteorShowers(): MeteorShowers | null { return this.meteorShowers; }

  setSpacecraftVisible(visible: boolean): void {
    if (this.spacecraft) this.spacecraft.setVisible(visible);
  }

  /**
   * Fade the entire heliocentric layer (sun + planets + moons + asteroids
   * + spacecraft + Lagrange overlay) by setting Group.visible. Used by the
   * scale-tier zoom-out animation: when the camera retreats to the local
   * stellar neighbourhood, the solar-system meshes are no longer
   * meaningfully visible (sub-pixel) and continuing to render them just
   * wastes draw calls and can produce z-fighting artefacts.
   *
   * Opacity 0 → group hidden; opacity > 0 → group visible. We don't try
   * to interpolate per-mesh material opacity here because that would
   * require touching every BodyMesh / OrbitLine / Belt material, and the
   * crossfade duration is short enough that a hard hide/show is fine.
   */
  setSolarSystemOpacity(opacity: number): void {
    this.heliocentric.visible = opacity > 0.05;
  }

  /** Local Group galaxies (LMC/SMC/M31 etc.) — share the galactic-tier
   *  fade with the disk. Slightly higher floor (0.4) so M31 / LMC stay
   *  legible while the disk is mid-transition rather than vanishing
   *  entirely. */
  setLocalGroupOpacity(opacity: number): void {
    this.localGroup?.setOpacity(Math.max(opacity, opacity > 0.05 ? 0.4 : 0));
  }

  /** Per-frame billboard update — keeps Local Group galaxies facing
   *  the camera. Called from main.ts alongside updateExoplanetHosts. */
  updateLocalGroup(cameraPos: import('three').Vector3): void {
    this.localGroup?.update(cameraPos);
  }

  /** Picking accessor for raycast hits. */
  getLocalGroup(): LocalGroupGalaxies | null { return this.localGroup; }

  /** Galactic-tier 3D Milky Way disk visibility. */
  setGalacticDiskOpacity(opacity: number): void {
    this.galacticDisk?.setOpacity(opacity);
  }

  /**
   * Neighbourhood-tier HYG point cloud visibility. Triggers lazy data
   * fetch on first non-zero opacity (idempotent) so the ~600 KB JSON
   * isn't paid by users who never zoom out.
   */
  setHygCloudOpacity(opacity: number): void {
    if (!this.hygCloud) return;
    if (opacity > 0 && !this.hygCloud.isLoaded()) {
      void this.hygCloud.load();
    }
    this.hygCloud.setOpacity(opacity);
  }

  /**
   * Toggle the Sharpless 2 emission-nebula layer. Lazy-loads
   * `/sharpless2.json` (~9 KB) on first enable; subsequent toggles
   * just flip the layer opacity.
   */
  setSharpless2Enabled(on: boolean): void {
    if (!this.sharpless) return;
    if (on && !this.sharpless.isLoaded()) {
      void this.sharpless.load();
    }
    this.sharpless.setOpacity(on ? 1.0 : 0);
  }

  /** Accessor for the screen-space pick handler in main.ts. Returns
   *  null when the toggle has never been turned on. */
  getSharplessLayer(): SharplessLayer | null { return this.sharpless; }
  getNGCFullLayer(): NGCFullLayer | null { return this.ngcFull; }

  /** Full NGC + IC catalogue (~11 000 entries, ~445 KB). Same lazy-
   *  load + opacity-toggle pattern as the Sharpless layer. */
  setNGCFullEnabled(on: boolean): void {
    if (!this.ngcFull) return;
    if (on && !this.ngcFull.isLoaded()) {
      void this.ngcFull.load();
    }
    this.ngcFull.setOpacity(on ? 0.85 : 0);
  }

  /** Abell galaxy-cluster catalogue (2712 entries, ~80 KB). */
  setAbellEnabled(on: boolean): void {
    if (!this.abell) return;
    if (on && !this.abell.isLoaded()) {
      void this.abell.load();
    }
    this.abell.setOpacity(on ? 0.8 : 0);
  }
  getAbellLayer(): AbellLayer | null { return this.abell; }
  getScopeReticle(): ScopeReticle | null { return this.scopeReticle; }

  /**
   * Toggle between the default (mag ≤ 7) and deep (mag ≤ 9) HYG
   * star catalogue. Lazy-fetches the deep variant on first request;
   * swapping back to default just changes the URL load target and
   * the geometry buffer gets replaced. ~3 MB download once per
   * session for deep mode.
   */
  setDeepStarsEnabled(deep: boolean): void {
    if (!this.hygCloud) return;
    const url = deep ? '/stars-hyg-3d-deep.json' : '/stars-hyg-3d.json';
    if (this.hygCloud.currentUrl() === url) return;
    // Force a re-load even if already loaded with the other URL.
    void this.hygCloud.load(url);
  }

  /** Exoplanet host halos — same opacity gate as the HYG cloud. */
  setExoplanetHostsOpacity(opacity: number): void {
    this.exoplanetHosts?.setOpacity(opacity);
  }

  /** Per-frame: keep host halos billboard-facing the camera. */
  updateExoplanetHosts(cameraPos: Vector3): void {
    this.exoplanetHosts?.update(cameraPos);
  }

  /** Pickable halo meshes for the main raycast handler. */
  getExoplanetHostPickables(): import('three').Object3D[] {
    return this.exoplanetHosts?.pickables ?? [];
  }

  getExoplanetHosts(): ExoplanetHosts | null {
    return this.exoplanetHosts;
  }

  /**
   * Layer-visibility snapshot captured the moment we land on an
   * exoplanet system, so deactivate() can restore exactly what was on.
   */
  private exoVisibilitySnapshot: Map<string, boolean> | null = null;

  /**
   * All scene-level layers that distract from a clean exoplanet system
   * close-up: HYG cloud, star labels, IAU boundaries, Messier markers,
   * other host halos, the 3D Milky Way disk, etc. We hide them on
   * activate() and restore on deactivate().
   *
   * Note: we deliberately keep `realStarfield` + the random `starfield`
   * because some sparse stars in the background look better than total
   * black, and they aren't labelled.
   */
  private getExoSceneLayers(): Array<{ key: string; obj: { visible: boolean } | null }> {
    return [
      { key: 'starMap.points', obj: this.starMap?.points ?? null },
      { key: 'starMap.lines', obj: this.starMap?.lines ?? null },
      { key: 'starMap.labels', obj: this.starMap?.labels ?? null },
      { key: 'messierLayer', obj: this.messierLayer?.object ?? null },
      { key: 'iauBoundaries', obj: this.iauBoundaries?.object ?? null },
      { key: 'milkyWay', obj: this.milkyWay?.mesh ?? null },
      { key: 'meteorShowers', obj: this.meteorShowers?.object ?? null },
      { key: 'cometTails', obj: this.cometTails?.object ?? null },
      { key: 'celestialGrids', obj: this.celestialGrids?.object ?? null },
      { key: 'lunarMansions', obj: this.lunarMansions?.object ?? null },
      { key: 'galacticDisk', obj: this.galacticDisk?.group ?? null },
      { key: 'localGroup', obj: this.localGroup?.group ?? null },
      { key: 'hygCloud', obj: this.hygCloud?.group ?? null },
      { key: 'exoplanetHosts', obj: this.exoplanetHosts?.group ?? null },
    ];
  }

  /**
   * Activate one of the 12 curated exoplanet systems (Trappist-1,
   * Proxima, Kepler-90, etc.). Hides the solar-system meshes AND every
   * other scene-level layer that would clutter the view (HYG point
   * cloud, star labels, other host halos, Milky Way disk, …) so the
   * camera lands on a clean stage with just the host + its planets.
   */
  activateExoplanetSystem(id: string): boolean {
    if (!this.exoplanetView) return false;
    const meta = this.exoplanetView.activate(id);
    if (!meta) return false;
    // Hide our solar system while landed on someone else's.
    this.heliocentric.visible = false;
    // Hide everything else that would otherwise sit between the camera
    // and the host (the bug: HYG labels like 'α Cen' were stealing the
    // visual focus from the actual M-dwarf at scene origin).
    const snap = new Map<string, boolean>();
    for (const { key, obj } of this.getExoSceneLayers()) {
      if (!obj) continue;
      snap.set(key, obj.visible);
      obj.visible = false;
    }
    this.exoVisibilitySnapshot = snap;
    return true;
  }

  /** Return to solar-system view. */
  deactivateExoplanetSystem(): void {
    this.exoplanetView?.deactivate();
    this.heliocentric.visible = true;
    // Restore every layer to whatever state it had before we landed.
    if (this.exoVisibilitySnapshot) {
      for (const { key, obj } of this.getExoSceneLayers()) {
        if (!obj) continue;
        const wasVisible = this.exoVisibilitySnapshot.get(key);
        if (wasVisible !== undefined) obj.visible = wasVisible;
      }
      this.exoVisibilitySnapshot = null;
    }
  }

  isExoplanetSystemActive(): boolean {
    return this.exoplanetView?.isActive() ?? false;
  }

  /** Show/hide habitable-zone discs across every cached exoplanet system.
   *  Driven by the Realism panel "Habitable zone overlay" checkbox. */
  setExoplanetHabitableZoneVisible(visible: boolean): void {
    this.exoplanetView?.setHabitableZoneVisible(visible);
  }

  getActiveExoplanetSystemId(): string | null {
    return this.exoplanetView?.getActiveId() ?? null;
  }

  /** Per-frame: advance the active exoplanet system's planets. */
  updateExoplanetSystem(jd: number): void {
    this.exoplanetView?.update(jd);
  }

  setLagrangePointsVisible(visible: boolean): void {
    if (this.lagrangePoints) {
      this.lagrangePoints.setVisible(visible);
      // Force one update so markers snap to current positions when shown.
      if (visible) this.lagrangePoints.update(this.lastJd);
    }
  }

  getLagrangePoints(): LagrangePointsLayer | null {
    return this.lagrangePoints;
  }

  setSatellitesVisible(visible: boolean): void {
    if (this.satellites) this.satellites.setVisible(visible);
  }

  /**
   * Switch every heliocentric body's propagator to a shared N-body simulation
   * sampled from the current Kepler state at `jd`. Reverse with disableNBody().
   */
  enableNBody(jd: number): void {
    if (this.nbodySim) return;
    const allDescs = [...STARS_AND_PLANETS, ...DWARFS];
    this.nbodySim = buildNBodyFromDescriptors(allDescs, jd);
    for (const d of allDescs) {
      if (d.id === 'sun') continue;
      this.originalPropagators.set(d.id, d.propagator);
      (d as { propagator: import('../physics/types').OrbitPropagator | null })
        .propagator = new NBodyAdapter(this.nbodySim, d.id, d.propagator);
    }
  }

  disableNBody(): void {
    if (!this.nbodySim) return;
    for (const [id, prop] of this.originalPropagators) {
      const d = [...STARS_AND_PLANETS, ...DWARFS].find(x => x.id === id);
      if (d) (d as { propagator: import('../physics/types').OrbitPropagator | null }).propagator = prop;
    }
    this.originalPropagators.clear();
    this.nbodySim = null;
  }

  isNBodyEnabled(): boolean { return this.nbodySim !== null; }

  /**
   * Switch the N-body integrator. 'verlet' = 2nd order (default, fast);
   * 'yoshida4' = 4th order symplectic (slower but ~10× more accurate).
   * No-op if N-body is not currently enabled.
   */
  setNBodyIntegrator(kind: 'verlet' | 'yoshida4'): void {
    if (this.nbodySim) this.nbodySim.integrator = kind;
  }
  /**
   * Toggle post-Newtonian (Schwarzschild) correction on the N-body sim.
   * Adds Mercury's 43"/century perihelion precession + smaller GR effects
   * on the other inner planets. Negligible cost.
   */
  setNBodyRelativistic(enabled: boolean): void {
    if (this.nbodySim) this.nbodySim.relativisticGR = enabled;
  }
  /**
   * Toggle Sun J2 (oblateness) perturbation on the N-body sim. Small
   * effect (~3″/century on Mercury perihelion) but historically the
   * "other half" of the Mercury anomaly discussion.
   */
  setNBodySolarJ2(enabled: boolean): void {
    if (this.nbodySim) this.nbodySim.solarJ2 = enabled;
  }
  getNBodyIntegrator(): 'verlet' | 'yoshida4' {
    return this.nbodySim?.integrator ?? 'verlet';
  }
  getNBodyRelativistic(): boolean {
    return this.nbodySim?.relativisticGR ?? false;
  }
  getNBodySolarJ2(): boolean {
    return this.nbodySim?.solarJ2 ?? false;
  }
  /** Returns the live N-body simulation, or null if not currently enabled.
   *  Used by the diagnostics panel to read conservation quantities. */
  getNBodySimulation(): NBodySimulation | null { return this.nbodySim; }

  setBackgroundColor(color: Color): void {
    this.scene.background = color.clone();
  }

  getAtmosphereSky(): AtmosphereSky | null { return this.atmosphereSky; }
  getHosekWilkieSky(): HosekWilkieSky | null { return this.hosekWilkieSky; }
  /**
   * Swap which atmospheric scattering model paints the observer-mode sky.
   * Both meshes exist in the scene; we toggle visibility so the swap is
   * one frame with no allocation. Sun direction + zenith + camera position
   * are mirrored on each call so the inactive sky stays in sync — that way
   * a mid-twilight toggle doesn't snap colours.
   */
  setSkyModel(model: 'preetham' | 'hosek-wilkie'): void {
    this.skyModel = model;
    if (!this.atmosphereSky || !this.hosekWilkieSky) return;
    // Only flip visibility if the observer-mode sky is currently shown
    // at all — don't fight the heliocentric-view sky-off invariant.
    const anyVisible = this.atmosphereSky.sky.visible || this.hosekWilkieSky.mesh.visible;
    if (anyVisible) {
      this.atmosphereSky.setVisible(model === 'preetham');
      this.hosekWilkieSky.setVisible(model === 'hosek-wilkie');
    }
  }
  getSkyModel(): 'preetham' | 'hosek-wilkie' { return this.skyModel; }

  /**
   * Show / move the location pin on Earth at given lat/lon.
   * Pin auto-attaches to Earth's mesh, inheriting its rotation and tilt.
   */
  setLocationPin(latDeg: number, lonDeg: number): void {
    const earth = this.bodies.get('earth');
    if (!earth) return;
    if (!this.locationPin) this.locationPin = new LocationPin();
    const r = this.scaler.radiusKm(earth.descriptor.physical.radiusKm);
    this.locationPin.attachTo(earth.mesh.mesh, r);
    this.locationPin.setLatLon(latDeg, lonDeg);
    this.locationPin.show();
  }

  hideLocationPin(): void {
    if (this.locationPin) this.locationPin.hide();
  }

  /** Update pin's body radius when scale mode changes. */
  refreshLocationPin(): void {
    if (!this.locationPin) return;
    const earth = this.bodies.get('earth');
    if (!earth) return;
    const r = this.scaler.radiusKm(earth.descriptor.physical.radiusKm);
    this.locationPin.setBodyRadius(r);
  }

  /**
   * Compute world-space position of a body for camera-follow. Walks up the
   * parent chain since moons are nested under planet groups.
   */
  getWorldPosition(id: string, target: Vector3): Vector3 | null {
    const entry = this.bodies.get(id);
    if (!entry) return null;
    return entry.mesh.group.getWorldPosition(target);
  }

  /** Body's current scene-units radius (depends on scale mode). */
  getBodyRadius(id: string): number | null {
    const entry = this.bodies.get(id);
    if (!entry) return null;
    const r = this.scaler.radiusKm(entry.descriptor.physical.radiusKm);
    return entry.descriptor.id === 'sun' ? r * this.scaler.sunMultiplier() : r;
  }

  /**
   * Pick a body by projecting each body's centre to screen-space and finding
   * the closest one within `thresholdPx` of the cursor. Solves the problem
   * that geometrical raycast hits are essentially impossible for sub-pixel
   * dwarf planets and moons, especially in real scale.
   */
  private readonly _tmpPickPos = new Vector3();
  private readonly _tmpPickProj = new Vector3();
  pickByScreenPosition(
    cursorX: number,
    cursorY: number,
    camera: import('three').Camera,
    canvasRect: { left: number; top: number; width: number; height: number },
    thresholdPx: number = 30,
  ): string | null {
    let bestId: string | null = null;
    let bestScore = thresholdPx;
    let bestDepth = Infinity;

    for (const entry of this.bodies.values()) {
      entry.mesh.group.getWorldPosition(this._tmpPickPos);
      this._tmpPickProj.copy(this._tmpPickPos).project(camera);
      // Behind near plane or outside NDC z range.
      if (this._tmpPickProj.z < -1 || this._tmpPickProj.z > 1) continue;

      const sx = (this._tmpPickProj.x + 1) * 0.5 * canvasRect.width + canvasRect.left;
      const sy = (-this._tmpPickProj.y + 1) * 0.5 * canvasRect.height + canvasRect.top;
      const dist = Math.hypot(sx - cursorX, sy - cursorY);

      if (dist < bestScore) {
        const depth = this._tmpPickPos.distanceToSquared(camera.position);
        bestScore = dist;
        bestDepth = depth;
        bestId = entry.descriptor.id;
      } else if (dist < thresholdPx && dist === bestScore) {
        const depth = this._tmpPickPos.distanceToSquared(camera.position);
        if (depth < bestDepth) {
          bestDepth = depth;
          bestId = entry.descriptor.id;
        }
      }
    }
    return bestId;
  }
}

function orbitColor(body: BodyDescriptor): number {
  switch (body.category) {
    case 'planet': return 0x5a8ec2;
    case 'dwarf': return 0x9c7aa8;
    case 'comet': return 0x6fc5d8;
    default: return 0x4a6a8a;
  }
}
